import { useCallback, useEffect, useState, useMemo } from "react";
import ReactTableWrapper from "../../components/ReactTableWrapper";
import PremiumPageHeader from "../../components/PremiumPageHeader";
import { ListPageFloatingControls } from "../../components/ListPageLayout";
import ListViewSettings from "../../components/ListViewSettings";
import { exportAsCSV } from "../../utils/CSVExporter";
import { exportAsPDF } from "../../utils/PDFExporter";
import { openPdfResponse } from "../../utils/openPdfInApp";
import { useNavigate } from "react-router-dom";
import { Box, Typography } from '@mui/material';
import "../../App.css";
import Sidebar from '../../Sidebar';
import Swal from "sweetalert2";
import { useCookies } from 'react-cookie';
import { getAllQuotations, deleteQuotation, fetchPDF } from "../../services/QuotationServices";
import { getListViewSettings } from "../../services/SettingsServices";
import { displayError } from "../../helpers";
import { buildQuotationQueryParams } from "../../utils/listViewDateRanges";
import moment from 'moment';

function QuotationTable() {
    const InitialData = { records: [] };
    const [data, setData] = useState(InitialData);
    const navigate = useNavigate();
    const [refreshKey, setRefreshKey] = useState(0);
    const [cookies] = useCookies(['myToken']);
    const [isLoading, setIsLoading] = useState(true);
    const left_pinned = ['quotation_no'];
    const right_pinned = ['id'];

    const todayDateLabel = moment().format('DD-MM-YYYY');

    const summaryTotals = useMemo(() => {
        const todayRecords = data.records.filter(
            (row) => row.quotation_date === todayDateLabel
        );
        return {
            total_count: data.records.length,
            today_count: todayRecords.length,
            total_qty: data.records.reduce((sum, row) => sum + (Number(row.quantity) || 0), 0),
            total_amount: data.records.reduce((sum, row) => sum + (Number(row.amount) || 0), 0),
        };
    }, [data.records, todayDateLabel]);

    const todayQuoteCard = useMemo(() => ({
        label: 'Today Quote',
        value: String(summaryTotals.today_count),
        gradient: 'linear-gradient(135deg, #b45309 0%, #f59e0b 55%, #fbbf24 100%)',
        shadow: '0 8px 20px rgba(245, 158, 11, 0.28)',
    }), [summaryTotals.today_count]);

    const summaryCards = useMemo(() => ([
        {
            label: 'Total',
            value: String(summaryTotals.total_count),
            gradient: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 55%, #60a5fa 100%)',
            shadow: '0 8px 20px rgba(37, 99, 235, 0.24)',
        },
        {
            label: 'Total Qty',
            value: Number(summaryTotals.total_qty || 0).toFixed(2),
            gradient: 'linear-gradient(135deg, #4c1d95 0%, #7c3aed 55%, #a78bfa 100%)',
            shadow: '0 8px 20px rgba(124, 58, 237, 0.24)',
        },
        {
            label: 'Total Amount',
            value: Number(summaryTotals.total_amount || 0).toFixed(2),
            gradient: 'linear-gradient(135deg, #0f766e 0%, #0d9488 55%, #2dd4bf 100%)',
            shadow: '0 8px 20px rgba(13, 148, 136, 0.24)',
        },
    ]), [summaryTotals]);

    const columns = useMemo(
        () => [
            { fieldName: 'company_id', headerName: 'Company', size: 80 },
            { fieldName: 'quotation_no', headerName: 'Quote No', size: 88 },
            { fieldName: 'quotation_date', headerName: 'Date', size: 86 },
            { fieldName: 'customer_id', headerName: 'Customer', size: 120 },
            { fieldName: 'project_name', headerName: 'Project', size: 110 },
            { fieldName: 'product_id', headerName: 'Grade', size: 75 },
            { fieldName: 'quantity', headerName: 'Qty', size: 68, align: 'right' },
            { fieldName: 'rate', headerName: 'Rate', size: 68, align: 'right' },
            { fieldName: 'amount', headerName: 'Amount', size: 80, align: 'right' },
            { fieldName: 'tax', headerName: 'Tax', size: 70 },
            { fieldName: 'concrete_structure', headerName: 'Structure', size: 95 },
            { fieldName: 'delivery_mode', headerName: 'Delivery', size: 82 },
            { fieldName: 'salesrep_id', headerName: 'Marketing', size: 100 },
            { fieldName: 'status', headerName: 'Status', size: 72 },
            { fieldName: 'id', headerName: 'ROW_ACTIONS' },
        ],
        [],
    );

    const loadQuotations = useCallback((config) => {
        if (!config) return;
        setIsLoading(true);
        getAllQuotations(cookies, buildQuotationQueryParams(config))
            .then((quotationList) => {
                const tableData = (quotationList.quotation_list || []).map((quotation) => ({
                    id: quotation.id,
                    company_id: quotation.company?.alias ?? '',
                    product_id: quotation.product?.name ?? '',
                    quotation_no: `${quotation.prefix || ''}${quotation.quotation_no || ''}`,
                    quotation_date: quotation.quotation_date,
                    customer_id: quotation.customer?.name ?? '',
                    salesrep_id: quotation.customer?.salesrep?.name ?? '',
                    consignee_id: quotation.consignee?.name ?? '',
                    quantity: quotation.quantity,
                    rate: quotation.rate,
                    amount: quotation.amount,
                    status: quotation.status ? 'active' : 'inactive',
                    project_name: quotation.consignee?.project_name ?? '',
                    tax: quotation.tax?.name ?? '',
                    concrete_structure: quotation.concrete_structure?.name ?? '',
                    delivery_mode: (quotation.delivery_mode ?? '').toUpperCase(),
                }));
                setData({ records: tableData });
                setIsLoading(false);
            })
            .catch((error) => {
                displayError(error?.response?.data ?? error.message, 'Failed to load quotation list');
                setData({ records: [] });
                setIsLoading(false);
            });
    }, [cookies]);

    const handleSettingsSaved = useCallback((config) => {
        loadQuotations(config);
    }, [loadQuotations]);

    const onEditQuotation = (quotationId) => {
        navigate(`/QuotationEdit/${quotationId}/`);
    };

    const onDeleteQuotation = (quotationId, event) => {
        event.preventDefault();
        Swal.fire({
            title: 'Are you sure to Delete?',
            showCancelButton: true,
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
        }).then((result) => {
            if (result.isConfirmed) {
                setIsLoading(true);
                deleteQuotation(cookies, quotationId)
                    .then(() => {
                        Swal.fire('Deleted Successfully!', '', 'success');
                        setRefreshKey((oldKey) => oldKey + 1);
                    })
                    .catch((error) => {
                        setIsLoading(false);
                        displayError(error?.response?.data ?? error.message, 'Delete Failed');
                    });
            } else if (result.isDismissed) {
                Swal.fire('Not Deleted', '', 'info');
            }
        });
    };

    const handlePrint = (quotationId, event) => {
        event.preventDefault();
        setIsLoading(true);
        fetchPDF(cookies, quotationId)
            .then((response) => {
                openPdfResponse(response, {
                    title: 'Quotation',
                    downloadFileName: 'quotation.pdf',
                });
                setIsLoading(false);
            })
            .catch((error) => {
                displayError(error, 'Custom print/export failed');
                setIsLoading(false);
            });
    };

    const handleCSVExport = () => {
        exportAsCSV(
            columns.filter((col) => col.headerName !== 'ROW_ACTIONS').map((c) => c.headerName),
            data.records.map((quotationcsv) => ({
                company_id: quotationcsv.company_id,
                product_id: quotationcsv.product_id,
                quotation_no: quotationcsv.quotation_no,
                quotation_date: quotationcsv.quotation_date,
                customer_id: quotationcsv.customer_id,
                salesrep_id: quotationcsv.salesrep_id,
                quantity: quotationcsv.quantity,
                rate: quotationcsv.rate,
                amount: quotationcsv.amount,
                status: quotationcsv.status,
            })),
        );
    };

    const handlePDFExport = () => {
        exportAsPDF(
            'List of Quotation',
            columns
                .filter((col) => col.headerName !== 'ROW_ACTIONS')
                .map((col) => ({ header: col.headerName, dataKey: col.fieldName })),
            data.records,
            'quotation.pdf',
        );
    };

    useEffect(() => {
        getListViewSettings(cookies, 'quotation')
            .then((response) => {
                loadQuotations(response.config || {});
            })
            .catch((error) => {
                displayError(error?.response?.data ?? error.message, 'Failed to load list settings');
            });
    }, [cookies, loadQuotations, refreshKey]);

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <div id="page-wrap">
                <ListPageFloatingControls
                    formLink="/Quotation"
                    addNewLabel="New Quotation"
                    homeLink="/Home"
                />
                <div className="container">
                    <PremiumPageHeader
                        module="MARKETING"
                        title="List of Quotation"
                        subtitle="Configure filters in General Settings. The table loads based on your saved preferences."
                        modeLabel="VIEW MODE"
                        headerActions={
                            <ListViewSettings
                                viewKey="quotation"
                                onSaved={handleSettingsSaved}
                            />
                        }
                    />
                </div>

                <Box
                    className="container"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        flexWrap: { xs: 'wrap', lg: 'nowrap' },
                        mb: 2.5,
                        py: 1,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexShrink: 0, flexWrap: 'wrap' }}>
                        <Box
                            sx={{
                                flex: '0 0 auto',
                                minWidth: 128,
                                borderRadius: '12px',
                                background: todayQuoteCard.gradient,
                                boxShadow: todayQuoteCard.shadow,
                                px: 1.75,
                                py: 1.1,
                                color: '#fff',
                                whiteSpace: 'nowrap',
                                textAlign: 'right',
                            }}
                        >
                            <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', opacity: 0.9, lineHeight: 1.2 }}>
                                {todayQuoteCard.label}
                            </Typography>
                            <Typography sx={{ fontSize: '1.35rem', fontWeight: 700, lineHeight: 1.15, letterSpacing: '-0.02em', mt: 0.25 }}>
                                {todayQuoteCard.value}
                            </Typography>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'stretch',
                            gap: 1.25,
                            flex: 1,
                            justifyContent: { xs: 'flex-start', lg: 'flex-end' },
                            minWidth: 0,
                            overflowX: 'auto',
                        }}
                    >
                        {summaryCards.map((card) => (
                            <Box
                                key={card.label}
                                sx={{
                                    flex: '0 0 auto',
                                    minWidth: 128,
                                    borderRadius: '12px',
                                    background: card.gradient,
                                    boxShadow: card.shadow,
                                    px: 1.75,
                                    py: 1.1,
                                    color: '#fff',
                                    whiteSpace: 'nowrap',
                                    textAlign: 'right',
                                }}
                            >
                                <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', opacity: 0.9, lineHeight: 1.2 }}>
                                    {card.label}
                                </Typography>
                                <Typography sx={{ fontSize: '1.35rem', fontWeight: 700, lineHeight: 1.15, letterSpacing: '-0.02em', mt: 0.25 }}>
                                    {card.value}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                <div className="container item-list-table-container">
                    <div className="table-responsive">
                        <ReactTableWrapper
                            title="LIST OF QUOTATION"
                            columns={columns}
                            data={data.records}
                            onRowEdit={onEditQuotation}
                            onRowDelete={onDeleteQuotation}
                            onCSVExport={handleCSVExport}
                            onPDFExport={handlePDFExport}
                            isLoading={isLoading}
                            hideDelete
                            onRowPrint={handlePrint}
                            hidePrint={false}
                            left_pinned={left_pinned}
                            right_pinned={right_pinned}
                            compactRows
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default QuotationTable;
